import { Box } from '@dtx-company/shared-components/src/components/atoms/Box/index'
import { DESKTOP_FOOTER_BREAKPOINT } from '../styles'
import { FC } from 'react'
import { FLOWCODE_MARKETING_BASE_URL } from '@dtx-company/true-common/src/constants/routes'
import { FooterIcon, FooterIcons, FooterLinks, FooterWrapper, MobileFooterWrapper } from './styles'
import { FooterLink } from './FooterLink'
import { FooterTypes, TTextLink } from '@dtx-company/true-common/src/types/footer'
import { GenericButton } from '@dtx-company/shared-components/src/components/atoms/Button/index'
import { RichText } from 'prismic-reactjs'
import { RootCodeState } from '@dtx-company/inter-app/src/redux/types'
import { Text } from '@dtx-company/shared-components/src/components/atoms/Text/index'
import { TextLink } from '@dtx-company/inter-app/src/utils/links'
import { media } from '@app/common/src/theme'
import { prismicImageLoader } from '@app/common/src/utils/prismicImageLoader'
import { useAppContent } from '../../../hooks/useAppContent'
import { useCodeSelector } from '@dtx-company/inter-app/src/hooks/useCodeSelector'
import { useIsMounted } from '@dtx-company/true-common/src/hooks/useIsMounted'
import { useMediaQuery } from '@dtx-company/true-common/src/hooks/useMediaQuery'
import NextImage from 'next/image'

interface LinkProps {
  title: string
  links: TTextLink[]
}

const COOKIE_PREFERENCES_LINK_ID = 'cookie-preferences'

const LinksSection: FC<LinkProps> = ({ title, links }: LinkProps) => {
  return (
    <Box flexDirection="column">
      <FooterLinks>
        <li>
          <Text color="primary.white" variant="body/small" fontWeight={600} py="4px">
            {title}
          </Text>
        </li>
        {links.map(({ text, link, link_id }, idx) => {
          const linkText = RichText.asText(text)
          const linkId = RichText.asText(link_id)
          if (linkId === COOKIE_PREFERENCES_LINK_ID) {
            return (
              link &&
              text && (
                <li key={linkText + idx}>
                  <GenericButton onClick={() => window.OneTrust?.ToggleInfoDisplay?.()}>
                    <Text as="span" color="primary.white">
                      {RichText.asText(text)}
                    </Text>
                  </GenericButton>
                </li>
              )
            )
          }

          return (
            linkText &&
            link &&
            text && (
              <li key={linkText + idx}>
                <TextLink color="primary.white" href={link.url}>
                  {RichText.asText(text)}
                </TextLink>
              </li>
            )
          )
        })}
      </FooterLinks>
    </Box>
  )
}

const FlowcodeLogo = ({ src, altText }: { src: string; altText: string }): JSX.Element => (
  <Box margin="4px 0 30px 0">
    <NextImage
      className="FlowcodeLogo"
      alt={altText}
      loader={prismicImageLoader}
      width={280}
      height={40}
      src={src}
      style={{ maxWidth: '100%', height: 'auto' }}
    />
  </Box>
)

const Trademark = ({ trademark }: { trademark: FooterTypes['trademark'] }): JSX.Element => {
  return (
    <Box
      flexDirection="row"
      justifyContent="flex-start"
      alignItems="center"
      flexWrap="wrap"
      width="100%"
      gridArea="trademark"
      mt={['52px', '52px', '52px', '0px']}
    >
      {trademark.map(({ text }, idx) => (
        <Text
          key={`string-${idx}`}
          color="primary.white"
          paddingRight="8px"
          fontSize={['12px', '14px']}
        >
          {RichText.asText(text)}
        </Text>
      ))}
    </Box>
  )
}

const FooterSocialLinks = ({
  socialmedia
}: {
  socialmedia: FooterTypes['socialmedia']
}): JSX.Element => {
  return (
    <FooterIcons>
      {socialmedia?.map(({ link, logo }, idx) => (
        <li key={`social-${logo.alt}-${idx}`}>
          <FooterIcon href={link.url} target="_blank" rel="noopener noreferrer">
            <NextImage height={24} width={24} alt={logo.alt} src={logo.url} />
          </FooterIcon>
        </li>
      ))}
    </FooterIcons>
  )
}

const DesktopFooter = ({ data }: { data: Partial<FooterTypes> }): JSX.Element => {
  return (
    <FooterWrapper data-testid="desktop-footer-wrapper">
      <Box bg="inherit" width="100%" flexDirection="column" gridArea="logo">
        {data?.logo && (
          <FooterLink as="a" href={FLOWCODE_MARKETING_BASE_URL} aria-label="Flowcode">
            <FlowcodeLogo src={data.logo.url} altText={data.logo.alt} />
          </FooterLink>
        )}
        {!!data?.socialmedia?.length && <FooterSocialLinks socialmedia={data.socialmedia} />}
      </Box>
      {!!data?.body?.length && (
        <Box
          padding={'0px 0px 0px 24px'}
          justifyContent="space-between"
          gridColumnGap="12px"
          width="100%"
          gridArea="links"
        >
          {data.body.map(({ primary, items }, idx) => {
            const title = RichText.asText(primary.title)
            return <LinksSection key={`${title}-links-${idx}`} title={title} links={items} />
          })}
        </Box>
      )}
      {!!data?.trademark?.length && <Trademark trademark={data.trademark} />}
    </FooterWrapper>
  )
}

export const MobileFooter = ({ data }: { data: Partial<FooterTypes> }): JSX.Element => (
  <MobileFooterWrapper data-testid="mobile-footer-wrapper" padding="80px 24px 24px">
    {data?.logo && (
      <FooterLink as="a" href={FLOWCODE_MARKETING_BASE_URL} aria-label="Flowcode">
        <FlowcodeLogo src={data.logo.url} altText={data.logo.alt} />
      </FooterLink>
    )}

    {!!data?.socialmedia?.length && <FooterSocialLinks socialmedia={data.socialmedia} />}

    {!!data?.mobilelinks?.length && (
      <Box marginTop="52px">
        <FooterLinks role="list" margin={0}>
          {data.mobilelinks.map(({ text, link, link_id }, idx) => {
            const linkId = RichText.asText(link_id)
            if (linkId === COOKIE_PREFERENCES_LINK_ID) {
              return (
                <li key={`${text}-${idx}`}>
                  <GenericButton
                    onClick={() => window.OneTrust?.ToggleInfoDisplay?.()}
                    display="inline"
                  >
                    <Text variant="body/small" as="span" color="primary.white" fontWeight={600}>
                      {text}
                    </Text>
                  </GenericButton>
                </li>
              )
            }

            return (
              <li key={`${text}-${idx}`}>
                <FooterLink as="a" href={link.url}>
                  <Text variant="body/small" color="white" fontWeight={600} as="span">
                    {text}
                  </Text>
                </FooterLink>
              </li>
            )
          })}
        </FooterLinks>
      </Box>
    )}
    {!!data?.trademark?.length && <Trademark trademark={data.trademark} />}
  </MobileFooterWrapper>
)

export const Footer: FC = () => {
  const { footer } = useAppContent()
  const isDesktop = useMediaQuery(media.custom(DESKTOP_FOOTER_BREAKPOINT))
  const isMounted = useIsMounted()
  const { hideFooter } = useCodeSelector((state: RootCodeState) => {
    return state.settings
  })

  const displayDesktopGenerator = !hideFooter && (!isMounted() || isDesktop)
  const displayMobileGenerator = !hideFooter && (!isMounted() || !isDesktop)

  return (
    <>
      {displayDesktopGenerator && <DesktopFooter data={footer} />}
      {displayMobileGenerator && <MobileFooter data={footer} />}
    </>
  )
}
